import React from 'react';
import { Framework } from '../../types/grc';
import { Clock, CheckCircle, FileText, Plus, Upload } from 'lucide-react';

interface FrameworkSelectorProps {
  frameworks: Framework[];
  selectedId: number | null;
  onSelect: (framework: Framework) => void;
  onAddNew: () => void;
}

export const FrameworkSelector: React.FC<FrameworkSelectorProps> = ({
  frameworks,
  selectedId,
  onSelect,
  onAddNew
}) => {
  const getFrameworkIcon = (code: string) => {
    switch (code.toUpperCase()) {
      case 'ISO27001': return '🛡️';
      case 'ISO27002': return '🔧';
      case 'GDPR': return '🇪🇺';
      case 'NIS2': return '🔐';
      case 'DORA': return '🏦';
      case 'ZIB': return '📜';
      case 'ZOINS': return '🎰';
      case 'CIS': return '⚙️';
      case 'NIST': return '🏛️';
      case 'SOC2': return '✅';
      case 'HIPAA': return '🏥';
      case 'PCI': case 'PCIDSS': return '💳';
      default: return '📋';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'regulation': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
      case 'standard': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
      case 'benchmark': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
    }
  };

  // Empty State
  if (frameworks.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 mx-auto mb-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
          <Upload className="w-12 h-12 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Nema Framework-a
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
          Dodajte novi standard, regulativu ili benchmark upload-om dokumenta. 
          AI će automatski analizirati i ekstraktovati sve zahteve i kontrole.
        </p>
        <button
          onClick={onAddNew}
          className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-semibold"
        >
          <Plus className="w-5 h-5" />
          Upload Framework
        </button>
        
        {/* Supported formats info */}
        <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl max-w-lg mx-auto">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Podržani tipovi dokumenata:
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span>🛡️</span> Standardi (ISO, NIST, CIS)
            </div>
            <div className="flex items-center gap-2">
              <span>🇪🇺</span> Regulative (GDPR, NIS2, DORA)
            </div>
            <div className="flex items-center gap-2">
              <span>⚙️</span> Benchmarks (CIS Benchmarks)
            </div>
            <div className="flex items-center gap-2">
              <span>📋</span> Custom Politike
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add New Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={onAddNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Dodaj Framework
        </button>
      </div>

      {/* Framework Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {frameworks.map(fw => {
          const isReady = fw.wizard_completed && fw.dashboard_enabled;
          const hasControls = (fw.total_controls || 0) > 0;
          const isSelected = selectedId === fw.id;

          return (
            <button
              key={fw.id}
              onClick={() => onSelect(fw)}
              className={`
                relative p-5 rounded-xl border-2 text-left transition-all duration-200
                ${isSelected 
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 ring-2 ring-indigo-500/20' 
                  : isReady
                    ? 'border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600 bg-white dark:bg-gray-800'
                    : hasControls
                      ? 'border-amber-300 dark:border-amber-700 hover:border-amber-400 dark:hover:border-amber-600 bg-white dark:bg-gray-800'
                      : 'border-gray-300 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-600 bg-white dark:bg-gray-800'
                }
                hover:shadow-lg hover:-translate-y-0.5
              `}
            >
              {/* Status Badge */}
              <div className={`
                absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold
                ${isReady 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                  : hasControls
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                }
              `}>
                {isReady ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Ready
                  </span>
                ) : hasControls ? (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Setup
                  </span>
                ) : (
                  <span>Procesira se</span>
                )}
              </div>

              {/* Icon & Code */}
              <div className="text-2xl mb-2">{getFrameworkIcon(fw.code)}</div>
              <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                {fw.code}
              </div>
              
              {/* Name (truncated) */}
              <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2" title={fw.name}>
                {fw.name}
              </div>

              {/* Type Badge */}
              {fw.framework_type && (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 ${getTypeColor(fw.framework_type)}`}>
                  {fw.framework_type === 'regulation' ? 'Regulativa' : 
                   fw.framework_type === 'standard' ? 'Standard' :
                   fw.framework_type === 'benchmark' ? 'Benchmark' : 'Custom'}
                </span>
              )}

              {/* Stats */}
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {fw.total_controls || 0} kontrola
                </span>
                {isReady && (fw.applicable_controls || 0) > 0 && (
                  <span className="text-green-600 dark:text-green-400">
                    {fw.applicable_controls} aktivno
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
